import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css, useTheme } from 'styled-components';

import { Flex, Box, Text } from 'Components/Base';
import Notification from 'Components/Notification';
import { getAppUIVariantConfig } from 'Util/uiVariantSet';

import { SIDEBAR_MAX_WIDTH } from '../constants';

import Hamburger from './Hamburger';

const VARIANT = {
  FLAT: 'FLAT',
  DYNAMIC: 'DYNAMIC',
};

const Label = styled(Text)`
  display: none;
  width: 100%;
  left: 0;
  right: 0;
  position: absolute;
  text-align: center;
`;

const Navbar = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  background: transparent;
  height: ${({ theme }) => theme?.space?.NAVBAR_HEIGHT + theme?.space?.APP_TOP_OFFSET}px;
  padding: ${({ theme }) => `8px ${theme?.space?.APP_PADDING_X}px`};
  padding-top: ${({
    theme: {
      space: { APP_TOP_OFFSET },
    },
  }) => (APP_TOP_OFFSET > 0 ? APP_TOP_OFFSET : 8)}px;

  ${({ $isScrolled, $variant, theme: { colors } }) => css`
    &::before {
      content: '';
      position: absolute;
      top: -1px;
      left: 0;
      width: 100%;
      height: 100%;
      background: ${colors?.navbarMyCourseBackground ?? colors.transparent};
      opacity: ${$isScrolled ? 1 : 0};
      transition: opacity 0.3s ease-in-out;
    }

    ${!$variant &&
    css`
      width: 100%;
      position: absolute;
      z-index: 1;
    `}

    ${$isScrolled &&
    css`
      position: fixed;
      top: 0;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05);
      ${Label} {
        display: block;
      }
    `}
  `};
`;

const IconContainer = styled(Flex)`
  position: fixed;
  top: 0;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  height: ${({ theme }) => theme?.space?.NAVBAR_HEIGHT + theme?.space?.APP_TOP_OFFSET}px;
  padding: ${({ theme }) => `8px ${theme?.space?.APP_PADDING_X}px`};
  padding-top: ${({
    theme: {
      space: { APP_TOP_OFFSET },
    },
  }) => (APP_TOP_OFFSET > 0 ? APP_TOP_OFFSET : 8)}px;
  z-index: 1000;
`;

const NAVBAR_MAX_WIDTH = `calc(100% - ${SIDEBAR_MAX_WIDTH})`;

const DynamicDashboardNavigation = ({ children }) => {
  const { space } = useTheme();
  const [isBackgroundActiveOnScroll, setIsBackgroundActiveOnScroll] = useState(
    window.scrollY >= space.NAVBAR_HEIGHT
  );

  useEffect(() => {
    const handleScroll = () =>
      setIsBackgroundActiveOnScroll(window.scrollY >= space.NAVBAR_HEIGHT / 2);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [space]);
  return (
    <Navbar maxWidth={{ lg: NAVBAR_MAX_WIDTH }} $isScrolled={isBackgroundActiveOnScroll}>
      {children}
    </Navbar>
  );
};

const DashboardNavigation = ({ variant = VARIANT.FLAT, children }) => {
  const { MyCourseTitle } = getAppUIVariantConfig();

  if (variant === VARIANT.FLAT) {
    return (
      <Navbar $variant={VARIANT.FLAT}>
        <Hamburger display={{ _: 'block', lg: 'none' }} color="primary" />
        <Box display={{ _: 'none', lg: 'block' }} />
        <Notification iconProps={{ bg: 'transparent', color: 'primary' }} />
      </Navbar>
    );
  }

  return (
    <>
      <DynamicDashboardNavigation>
        <Label textStyle={'subtitle1'} color={MyCourseTitle === 'Dark' ? 'text' : 'white'}>
          {children}
        </Label>
      </DynamicDashboardNavigation>
      <IconContainer width={{ _: 1, lg: NAVBAR_MAX_WIDTH }}>
        <Hamburger display={{ _: 'block', lg: 'none' }} color="hamburger" bg="hamburgerBg" />
        <Box display={{ _: 'none', lg: 'block' }} />
        <Notification iconProps={{ bg: 'notificationBg', color: 'notification' }} />
      </IconContainer>
    </>
  );
};

DashboardNavigation.propTypes = {
  variant: PropTypes.oneOf(Object.values(VARIANT)),
};

export { VARIANT };
export default DashboardNavigation;
